import { useCallback, useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ErrorDisplay } from '../components/ErrorDisplay';
import { Layout } from '../components/Layout';
import { WizardSteps } from '../components/WizardSteps';
import { Button } from '../components/ui';
import { SparkleIcon, DropArrowIcon, CheckIcon } from '../components/Icons';
import { resolveRecipient } from '../services/ens';
import { sendAnonymousFile } from '../services/swarm/honestInbox';
import { writeToInbox, findNextSlot } from '../services/swarm/gsoc';
import { requestSponsoredStamp } from '../services/swarm/stamps';
import { logger } from '../utils/logger';
import type { InboxParams } from '../types';

type UploadState = 'loading' | 'not-found' | 'idle' | 'confirm' | 'uploading' | 'success';

// Wizard steps for honest inbox
const dropboxSteps = [
  { number: 1, label: 'Select File' },
  { number: 2, label: 'Confirm' },
  { number: 3, label: 'Upload' },
  { number: 4, label: 'Summary' },
];

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function Dropbox() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>('loading');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Recipient state
  const [recipientPublicKey, setRecipientPublicKey] = useState<string | null>(null);
  const [recipientInboxParams, setRecipientInboxParams] = useState<InboxParams | null>(null);
  const [recipientEnsName, setRecipientEnsName] = useState<string | null>(null);

  // Look up recipient on mount
  useEffect(() => {
    if (!username) {
      setUploadState('not-found');
      return;
    }

    // Reject clearly invalid usernames (contain dots, slashes, or are too long)
    if (username.includes('.') || username.includes('/') || username.length > 64) {
      navigate('/404', { replace: true });
      return;
    }

    const lookupRecipient = async () => {
      logger.debug('[Dropbox] Looking up recipient:', username);

      try {
        const result = await resolveRecipient(username);
        logger.debug('[Dropbox] Recipient lookup result:', result);

        if (result.publicKey) {
          setRecipientPublicKey(result.publicKey);
          setRecipientEnsName(result.ensName);
          setRecipientInboxParams(result.inboxParams || null);
          setUploadState('idle');

          logger.debug('[Dropbox] Recipient found:', result.ensName);
        } else {
          logger.warn('[Dropbox] Recipient not found or no public key');
          setUploadState('not-found');
        }
      } catch (error) {
        logger.error('[Dropbox] Lookup error:', error);
        setUploadState('not-found');
      }
    };

    lookupRecipient();
  }, [username]);

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    setUploadState('confirm');
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const startUpload = async () => {
    if (!selectedFile || !recipientPublicKey) {
      logger.error('[Dropbox] No file or recipient public key');
      return;
    }

    setUploadState('uploading');
    setUploadProgress(0);
    setUploadError(null);

    try {
      // Fetch free stamp for anonymous sending
      let freeStampId: string | undefined;
      try {
        const sponsored = await requestSponsoredStamp();
        freeStampId = sponsored.batchId;
        logger.debug('[Dropbox] Got free stamp:', freeStampId?.slice(0, 12));
      } catch (stampErr) {
        logger.warn('[Dropbox] Failed to fetch free stamp:', stampErr);
      }

      logger.debug('[Dropbox] Starting anonymous upload to', recipientEnsName);

      // Send encrypted file anonymously (pass free stamp for upload)
      const result = await sendAnonymousFile(selectedFile, recipientPublicKey, {
        onProgress: (p) => {
          setUploadProgress(p);
        },
        onStatusChange: (status) => {
          logger.debug('[Dropbox] Status:', status);
          if (status === 'encrypting') setUploadProgress(5);
          if (status === 'uploading') setUploadProgress(10);
          if (status === 'complete') setUploadProgress(90);
        },
        stampId: freeStampId,
      });

      logger.debug('[Dropbox] File uploaded, reference:', result.reference.slice(0, 12));

      // Notify recipient via GSOC (pass free stamp for GSOC write)
      if (recipientInboxParams) {
        logger.debug('[Dropbox] Notifying recipient via GSOC...');
        try {
          const nextSlot = await findNextSlot(recipientInboxParams);
          logger.debug('[Dropbox] GSOC next slot:', nextSlot);

          await writeToInbox(
            recipientInboxParams,
            nextSlot,
            {
              reference: result.reference,
              filename: selectedFile.name,
              size: selectedFile.size,
            },
            { anonymous: true, stampId: freeStampId }
          );
          logger.debug('[Dropbox] GSOC notification sent at slot', nextSlot);
        } catch (gsocError) {
          logger.error('[Dropbox] GSOC notification failed:', gsocError);
          // Continue — file is uploaded, recipient can still find it via direct link
        }
      } else {
        logger.warn('[Dropbox] No inbox params for recipient — GSOC notification skipped');
      }

      logger.debug('[Dropbox] Complete! File sent to', recipientEnsName);
      setUploadProgress(100);
      setUploadState('success');
    } catch (error) {
      logger.error('[Dropbox] Upload failed:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed. Please try again.');
      setUploadState('idle');
    }
  };

  const displayFile = selectedFile;

  // Get theme based on state
  const getTheme = () => {
    if (uploadState === 'success') return 'dark';
    if (uploadState === 'not-found') return 'white';
    return 'blue';
  };

  // Get wizard step number
  const getWizardStep = (): number => {
    switch (uploadState) {
      case 'idle': return 1;
      case 'confirm': return 2;
      case 'uploading': return 3;
      case 'success': return 4;
      default: return 1;
    }
  };

  const renderContent = () => {
    switch (uploadState) {
      case 'loading':
        return (
          <div className="content-centered">
            <h1 className="heading-lg">Looking up {username}...</h1>
          </div>
        );

      case 'not-found':
        return (
          <div className="content-centered">
            <h1 className="heading-lg" style={{ color: 'var(--text-primary)' }}>
              User not found
            </h1>
            <p style={{ marginTop: 16, color: 'var(--text-secondary)' }}>
              The user "{username}" doesn't exist or hasn't set up their inbox.
            </p>
            <Button
              variant="primary"
              style={{ marginTop: 32 }}
              onClick={() => navigate('/')}
            >
              Go Home
            </Button>
          </div>
        );

      case 'confirm':
        return (
          <div className="content-centered">
            <h1 className="heading-lg">Send to {username}</h1>
            <p style={{ fontSize: 18, marginTop: 8, opacity: 0.8 }}>
              Anonymous file transfer
            </p>

            <div style={{
              width: '100%',
              maxWidth: 500,
              marginTop: 32,
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              <div className="detail-row">
                <span className="detail-row-label">File name</span>
                <span>{displayFile?.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-row-label">Size</span>
                <span>{displayFile ? formatFileSize(displayFile.size) : ''}</span>
              </div>
              <div className="detail-row">
                <span className="detail-row-label">Recipient</span>
                <span>{recipientEnsName || username}</span>
              </div>
              <div className="detail-row">
                <span className="detail-row-label">Encryption</span>
                <span>End-to-end encrypted</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 32 }}>
              <Button variant="outline" onClick={startUpload}>
                Send File
              </Button>
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={() => setUploadState('idle')}
              >
                <span style={{ fontSize: 14 }}>✕</span> Cancel
              </button>
            </div>
          </div>
        );

      case 'uploading':
        return (
          <div className="content-centered">
            <h2 style={{ fontSize: 28 }}>Uploading</h2>
            <p style={{ marginTop: 16, fontSize: 18 }}>
              {displayFile?.name} <span style={{ opacity: 0.6 }}>{displayFile ? formatFileSize(displayFile.size) : ''}</span>
            </p>
            <p style={{ marginTop: 24, fontSize: 48, fontWeight: 500 }}>
              {Math.min(100, Math.round(uploadProgress))}%
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="content-centered" style={{ color: 'var(--white)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              <CheckIcon style={{ width: 36, height: 36, display: 'block', flexShrink: 0 }} />
              <h2 style={{ fontSize: 32, lineHeight: 1, margin: 0 }}>Sent.</h2>
            </div>

            <p style={{ marginTop: 24, fontSize: 18 }}>
              {displayFile?.name} <span style={{ opacity: 0.6 }}>{displayFile ? formatFileSize(displayFile.size) : ''}</span>
            </p>

            <p style={{ marginTop: 16, opacity: 0.7 }}>
              File has been sent to {recipientEnsName || username}
            </p>

            <button
              className="link-button"
              style={{ marginTop: 48 }}
              onClick={() => navigate('/')}
            >
              Click to go back home.
            </button>
          </div>
        );

      default:
        return (
          <div
            className={`content-centered dropzone ${isDragging ? 'dropzone-active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {uploadError && (
              <ErrorDisplay
                error={uploadError}
                onRetry={() => { setUploadError(null); setUploadState('confirm'); }}
                onDismiss={() => setUploadError(null)}
              />
            )}
            <h1 className="heading-xl">Send a file anonymously to:</h1>
            <p style={{ fontSize: 24, marginBottom: 40 }}>{recipientEnsName || username}</p>

            <div
              className="file-action text-action"
              onClick={() => fileInputRef.current?.click()}
            >
              <SparkleIcon className="file-action-icon" />
              <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                select
              </span>
              <span style={{ margin: '0 8px' }}>or</span>
              <DropArrowIcon className="file-action-icon" />
              <span>drop a file</span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleInputChange}
              style={{ display: 'none' }}
            />

            {isDragging && (
              <div className="drop-indicator">Drop your file here</div>
            )}
          </div>
        );
    }
  };

  return (
    <Layout theme={getTheme()}>
      {renderContent()}
      {uploadState !== 'idle' && uploadState !== 'loading' && uploadState !== 'not-found' && (
        <WizardSteps steps={dropboxSteps} currentStep={getWizardStep()} />
      )}
    </Layout>
  );
}
